import React from 'react';
import clsx from 'clsx';

/**
 * The app's one tab bar.
 *
 * Each variant is a look already used in the app, reproduced exactly; all of
 * them take their colours from theme tokens, so tabs everywhere follow the
 * client's theme from this one file.
 *
 *  - `config`: large tabs with gradient text for the active one (agent,
 *    chatbot and marketplace configuration).
 *  - `underline`: tabs with an underline on the active one, in a bordered box.
 *  - `underline-icon`: compact underlined tabs with icons.
 *  - `pill`: boxed tabs; the active one gets a grey background.
 *  - `side`: a vertical menu (Settings).
 *  - `pill-tint`: small boxed tabs; the active one gets a light brand tint.
 *  - `underline-bold`: bold tabs with a brand-coloured underline on the
 *    active one.
 *  - `side-accent`: a vertical menu whose active item has a coloured left edge
 *    (chatbot configuration).
 */

export interface TabItem {
  id: string;
  label: React.ReactNode;
  icon?: React.ComponentType<{ className?: string }>;
}

export type TabsVariant =
  | 'config'
  | 'underline'
  | 'underline-icon'
  | 'underline-bold'
  | 'pill'
  | 'pill-tint'
  | 'side'
  | 'side-accent';

export interface TabsProps {
  items: TabItem[];
  value: string;
  onChange: (id: string) => void;
  variant?: TabsVariant;
  /** Classes for the outer box (spacing, width). */
  className?: string;
}

export const Tabs: React.FC<TabsProps> = ({ items, value, onChange, variant = 'underline', className }) => {
  switch (variant) {
    case 'config':
      return (
        <div className={clsx('min-w-0 overflow-x-auto rounded-xl bg-white pr-6 shadow-[0_1px_0_rgb(var(--c-e9ebf3,233_235_243))]', className)}>
          <div className="flex min-w-max items-center" role="tablist">
            {items.map((tab) => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={value === tab.id}
                onClick={() => onChange(tab.id)}
                className={`shrink-0 border-b px-[30px] py-4 text-lg leading-none transition-colors ${
                  value === tab.id
                    ? 'border-c-3683c6 bg-gradient-to-r from-c-805dc6 to-c-3683c6 bg-clip-text font-semibold text-transparent'
                    : 'border-transparent font-light text-c-1f1f1f hover:text-c-4f5480'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      );

    case 'underline':
      return (
        <div className={clsx('rounded-xl border border-agentic-gray-200/70 bg-white', className)}>
          <div className="border-b border-agentic-gray-200">
            <nav className="flex space-x-8 px-6 overflow-x-auto" aria-label="Tabs" role="tablist">
              {items.map((tab) => (
                <button
                  key={tab.id}
                  role="tab"
                  aria-selected={value === tab.id}
                  onClick={() => onChange(tab.id)}
                  className={`tab-title ${value === tab.id ? 'tab-title-active' : ''}`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>
        </div>
      );

    case 'underline-icon':
      return (
        <div className={clsx('bg-white rounded-xl border shadow-sm', className)}>
          <nav className="flex space-x-1 overflow-x-auto px-4 border-b border-gray-200" aria-label="Tabs" role="tablist">
            {items.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  role="tab"
                  aria-selected={value === tab.id}
                  onClick={() => onChange(tab.id)}
                  className={`py-4 px-3 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${
                    value === tab.id
                      ? 'border-agentic-primary-blue text-agentic-primary-blue'
                      : 'border-transparent text-agentic-text-secondary hover:text-agentic-text-primary hover:border-gray-300'
                  }`}
                >
                  {Icon && <Icon className="h-4 w-4 inline mr-1.5" />}
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>
      );

    case 'pill':
      return (
        <nav className={clsx('flex gap-2 border-b border-agentic-gray-200 px-6 py-3', className)} aria-label="Tabs" role="tablist">
          {items.map((tab) => (
            <button
              key={tab.id}
              role="tab"
              aria-selected={value === tab.id}
              onClick={() => onChange(tab.id)}
              className={`rounded-lg px-3 py-2 text-sm font-medium ${
                value === tab.id ? 'bg-agentic-gray-100 text-agentic-text-primary' : 'text-agentic-text-secondary hover:bg-agentic-gray-50'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      );

    case 'pill-tint':
      return (
        <div className={clsx('flex items-center gap-1', className)} role="tablist">
          {items.map((tab) => (
            <button
              key={tab.id}
              role="tab"
              aria-selected={value === tab.id}
              onClick={() => onChange(tab.id)}
              className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
                value === tab.id ? 'bg-kv-agentic-blue/10 text-kv-agentic-blue' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      );

    case 'underline-bold':
      return (
        <div className={clsx('flex border-b border-agentic-gray-200', className)} role="tablist">
          {items.map((tab) => (
            <button
              key={tab.id}
              role="tab"
              aria-selected={value === tab.id}
              onClick={() => onChange(tab.id)}
              className={`px-5 py-3 text-sm font-semibold transition-colors border-b-2 -mb-px ${
                value === tab.id
                  ? 'border-kv-agentic-blue text-kv-agentic-blue'
                  : 'border-transparent text-agentic-text-muted hover:text-agentic-text-secondary'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      );

    case 'side':
      return (
        <aside className={clsx('h-fit rounded-2xl border border-agentic-gray-200 bg-white p-3 shadow-sm', className)}>
          <nav className="space-y-1" role="tablist" aria-orientation="vertical">
            {items.map((tab) => {
              const Icon = tab.icon;
              const isActive = value === tab.id;
              return (
                <button
                  key={tab.id}
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => onChange(tab.id)}
                  className={`flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left transition-colors ${
                    isActive
                      ? 'bg-agentic-gray-100 font-medium text-agentic-text-primary'
                      : 'text-agentic-text-secondary hover:bg-agentic-gray-50 hover:text-agentic-text-primary'
                  }`}
                >
                  {Icon && <Icon className="h-4 w-4" />}
                  <span className="truncate">{tab.label}</span>
                </button>
              );
            })}
          </nav>
        </aside>
      );

    case 'side-accent':
      return (
        <div className={clsx('w-full shrink-0 rounded-xl border border-agentic-gray-200/70 p-2 md:w-56', className)}>
          <nav className="flex flex-col gap-1" aria-label="Tabs" role="tablist" aria-orientation="vertical">
            {items.map((tab) => {
              const Icon = tab.icon;
              const isActive = value === tab.id;
              return (
                <button
                  key={tab.id}
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => onChange(tab.id)}
                  className={`flex items-center gap-2 rounded-lg border-l-2 px-3 py-2.5 text-left text-sm font-medium transition-colors ${
                    isActive
                      ? 'border-agentic-primary-blue bg-agentic-primary-blue/5 text-agentic-primary-blue'
                      : 'border-transparent text-agentic-text-secondary hover:bg-agentic-gray-100 hover:text-agentic-text-primary'
                  }`}
                >
                  {Icon && <Icon className="h-5 w-5 shrink-0" />}
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>
      );

    default:
      return null;
  }
};

export default Tabs;
